import { PortalReadState } from '../portal-read-state.tsx';
import { WorkPage, WorkSection, WorkEmpty } from '../../components/work-page.tsx';
import '../portal-page.css';
import { TableScroll } from '@web/components/table-scroll.tsx';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, AdminApiError } from '../../api/admin.ts';
import { Alert, Button, LoadingBlock } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import { formatCents } from '@shared/calculations/pricing.ts';

/**
 * The operator dashboard: MRR and the funnel as they stand (from the company
 * index), money-in from the revenue ledger, the MRR trend from the daily
 * snapshots, failed charges and top customers.
 *
 * The sparkline is deliberately decorative reinforcement of numbers that are
 * all present as text — single series, single brand hue, no axes to misread.
 */

function StatTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card">
      <p className="text-sm font-medium text-[color:var(--color-muted)]">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
      {sub ? <p className="mt-1 text-xs text-[color:var(--color-muted)]">{sub}</p> : null}
    </div>
  );
}

/** A single-series sparkline; the numbers it echoes are in the tiles above. */
function Sparkline({ points }: { points: number[] }) {
  if (points.length < 2) return null;
  const width = 560;
  const height = 96;
  const pad = 4;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const step = (width - pad * 2) / (points.length - 1);
  const path = points
    .map((value, index) => {
      const x = pad + index * step;
      const y = height - pad - ((value - min) / span) * (height - pad * 2);
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-24 w-full" aria-hidden="true" preserveAspectRatio="none">
      <path d={path} fill="none" stroke="var(--color-brand)" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

export default function AdminHomePage() {
  useDocumentTitle('Admin overview');
  const queryClient = useQueryClient();

  const overview = useQuery({
    queryKey: ['admin', 'metrics', 'overview'],
    queryFn: ({ signal }) => adminApi.metricsOverview(signal),
  });
  const history = useQuery({
    queryKey: ['admin', 'metrics', 'history'],
    queryFn: ({ signal }) => adminApi.metricsHistory(90, signal),
  });
  const failed = useQuery({
    queryKey: ['admin', 'metrics', 'failed'],
    queryFn: ({ signal }) => adminApi.failedCharges(signal),
  });
  const top = useQuery({
    queryKey: ['admin', 'metrics', 'top'],
    queryFn: ({ signal }) => adminApi.topCustomers(signal),
  });

  const [backfilling, setBackfilling] = useState(false);
  const [backfillNote, setBackfillNote] = useState<string | null>(null);
  // Where a bounded walk stopped, kept OUTSIDE the walk so the next click
  // resumes there instead of re-importing the first pages forever. Two
  // phases: paid history first, then the failures that pre-date the webhooks.
  const [backfillResume, setBackfillResume] = useState<{
    status: 'paid' | 'uncollectible';
    cursor: string | undefined;
  }>({ status: 'paid', cursor: undefined });
  const [error, setError] = useState<string | null>(null);

  async function runBackfill() {
    setBackfilling(true);
    setError(null);
    setBackfillNote(null);
    let { status, cursor } = backfillResume;
    try {
      let imported = 0;
      let pages = 0;
      // Bounded walk: fifty invoices a page, at most forty pages a click.
      while (pages < 40) {
        const result = await adminApi.backfillInvoices(status, cursor);
        imported += result.imported;
        pages += 1;
        if (result.nextCursor) {
          cursor = result.nextCursor;
        } else if (status === 'paid') {
          status = 'uncollectible';
          cursor = undefined;
        } else {
          setBackfillResume({ status: 'paid', cursor: undefined });
          setBackfillNote(`Backfill complete — ${imported} invoices imported this run.`);
          await queryClient.invalidateQueries({ queryKey: ['admin', 'metrics'] });
          return;
        }
      }
      setBackfillResume({ status, cursor });
      setBackfillNote(`Imported ${imported} invoices so far — press again to continue from where it stopped.`);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'metrics'] });
    } catch (caught) {
      // Progress up to the failing page survives, so retrying resumes there.
      setBackfillResume({ status, cursor });
      setError(
        caught instanceof AdminApiError
          ? caught.message
          : 'The backfill failed. Try again — it resumes where it stopped.',
      );
    } finally {
      setBackfilling(false);
    }
  }

  if (overview.isLoading) return <PortalReadState title="Overview" label="Loading metrics…" />;
  const data = overview.data;
  if (overview.isError || !data) {
    return (
      <PortalReadState title="Overview" error="The metrics could not be loaded." retry={() => overview.refetch()} />
    );
  }

  const money = (cents: number) => formatCents(cents, data.currencyCode);
  const snapshots = history.data ?? [];
  const mrrSeries = snapshots.map((snapshot) => snapshot.mrrCents);
  const oldest = snapshots[0];
  const churnedLast30d = (() => {
    const monthAgo = snapshots.length > 30 ? snapshots[snapshots.length - 31] : oldest;
    if (!monthAgo) return null;
    return Math.max(0, data.canceledCount - monthAgo.canceledCount);
  })();

  return (
    <div className="portal-page">
      <WorkPage
        title="Overview"
        description="Recurring revenue and the customer funnel, across every shard."
        actions={
          <Button busy={backfilling} onClick={() => void runBackfill()}>
            Backfill invoice history
          </Button>
        }
      >
        {error ? (
          <div className="mb-4">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}
        {backfillNote ? (
          <div className="mb-4">
            <Alert tone="success">{backfillNote}</Alert>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile label="MRR" value={money(data.mrrCents)} sub={`ARR ${money(data.arrCents)}`} />
          <StatTile
            label="Collected this month"
            value={money(data.paidThisMonthCents)}
            sub={`Last month ${money(data.paidLastMonthCents)}`}
          />
          <StatTile
            label="Paying companies"
            value={String(data.activeCount + data.pastDueCount)}
            sub={data.pastDueCount > 0 ? `${data.pastDueCount} past due` : 'None past due'}
          />
          <StatTile
            label="Trials"
            value={String(data.trialingCount)}
            sub={`${data.newLast30d} signups in 30 days${churnedLast30d !== null ? ` · ${churnedLast30d} churned` : ''}`}
          />
        </div>

        {history.isError ? (
          <Alert tone="error">
            Revenue history could not be loaded.{' '}
            <Button variant="ghost" onClick={() => void history.refetch()}>
              Try again
            </Button>
          </Alert>
        ) : null}
        {mrrSeries.length >= 2 ? (
          <div className="card mt-4">
            <div className="flex items-baseline justify-between">
              <h2 className="text-base font-semibold">MRR — last {snapshots.length} days</h2>
              <p className="text-sm text-[color:var(--color-muted)]">
                {oldest ? `${money(oldest.mrrCents)} → ${money(data.mrrCents)}` : null}
              </p>
            </div>
            <Sparkline points={mrrSeries} />
          </div>
        ) : null}

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <WorkSection title="Failed charges">
            {failed.isLoading ? (
              <LoadingBlock label="Loading…" />
            ) : failed.isError ? (
              <Alert tone="error">
                Failed charges could not be loaded.{' '}
                <Button variant="ghost" onClick={() => void failed.refetch()}>
                  Try again
                </Button>
              </Alert>
            ) : (failed.data?.length ?? 0) === 0 ? (
              <WorkEmpty title="No failed charges on record." />
            ) : (
              <TableScroll aria-label="Failed charges">
                <table className="data-table w-full min-w-max text-sm">
                  <thead>
                    <tr className="border-b border-[color:var(--color-line)] text-left text-[color:var(--color-muted)]">
                      <th scope="col" className="py-2 pr-3 font-medium">
                        Company
                      </th>
                      <th scope="col" className="py-2 pr-3 font-medium text-right">
                        Amount
                      </th>
                      <th scope="col" className="py-2 font-medium">
                        When
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(failed.data ?? []).slice(0, 8).map((charge) => (
                      <tr key={charge.invoiceId} className="border-b border-[color:var(--color-line)] last:border-b-0">
                        <td className="py-2 pr-3">
                          {charge.companyName ? (
                            <Link
                              to={`/admin/companies/${charge.companyId}`}
                              className="font-medium text-[color:var(--color-brand)] hover:underline"
                            >
                              {charge.companyName}
                            </Link>
                          ) : (
                            <span className="text-[color:var(--color-muted)]">{charge.companyId}</span>
                          )}
                          <div className="text-xs text-[color:var(--color-muted)]">{charge.ownerEmail ?? ''}</div>
                        </td>
                        <td className="py-2 pr-3 text-right font-medium">
                          {formatCents(charge.amountDueCents, charge.currency ?? data.currencyCode)}
                        </td>
                        <td className="py-2 text-[color:var(--color-muted)]">
                          {new Date(charge.occurredAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
          </WorkSection>

          <WorkSection title="Top customers by MRR">
            {top.isLoading ? (
              <LoadingBlock label="Loading…" />
            ) : top.isError ? (
              <Alert tone="error">
                Top customers could not be loaded.{' '}
                <Button variant="ghost" onClick={() => void top.refetch()}>
                  Try again
                </Button>
              </Alert>
            ) : (top.data?.byMrr.length ?? 0) === 0 ? (
              <WorkEmpty title="Nobody is paying yet." />
            ) : (
              <TableScroll aria-label="Top customers by MRR">
                <table className="data-table w-full min-w-max text-sm">
                  <thead>
                    <tr className="border-b border-[color:var(--color-line)] text-left text-[color:var(--color-muted)]">
                      <th scope="col" className="py-2 pr-3 font-medium">
                        Company
                      </th>
                      <th scope="col" className="py-2 pr-3 font-medium">
                        Plan
                      </th>
                      <th scope="col" className="py-2 font-medium text-right">
                        MRR
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(top.data?.byMrr ?? []).map((customer) => (
                      <tr
                        key={customer.companyId}
                        className="border-b border-[color:var(--color-line)] last:border-b-0"
                      >
                        <td className="py-2 pr-3">
                          <Link
                            to={`/admin/companies/${customer.companyId}`}
                            className="font-medium text-[color:var(--color-brand)] hover:underline"
                          >
                            {customer.companyName}
                          </Link>
                          <div className="text-xs text-[color:var(--color-muted)]">{customer.ownerEmail ?? ''}</div>
                        </td>
                        <td className="py-2 pr-3">
                          {customer.plan ?? '—'}
                          {customer.billingInterval === 'year' ? ' (yearly)' : ''}
                        </td>
                        <td className="py-2 text-right font-medium">{money(customer.mrrCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
          </WorkSection>
        </div>
      </WorkPage>
    </div>
  );
}
